import { ChangeDetectorRef, Injectable } from '@angular/core';
import { HttpService } from './http.service';
import { HttpErrorResponse, HttpEventType, HttpResponse } from '@angular/common/http';
import { HttpFunctionResult } from '../_interfaces/HttpFunctionResult';
import { FeedbackService, MsgType } from './feedback.service';
import { PopoverListItem, PopoverPage } from '../_template/popover/popover.page';
import { TicketEmployee } from './tickets/ticket.service';
import { TicketEditService } from './ticket-edit.service';
import { AuthService } from './auth.service';

import dayjs from '../_utils/dayjs.config';
import { BehaviorSubject, Observable } from 'rxjs';
import { EmailTag, EmailTags } from '../_template/ticket-detail/content-edit-popup/email-tags.service';
import { WorkstepContent } from '../_template/ticket-detail/workstep-content/workstep-content.component';
import { Attachment } from '../_interfaces/Attachment';

export interface Mailbox {
  id: number
  description: string
  templateId?: number
}

export interface MailTemplate {
  id: number
  description: string
  subject?: string;
}

export interface SendingWorkstepMail{
  mailboxId: number
  to: string[]
  cc: string[]
  bcc: string[]
  subject: string
  html: string
  plainText: string
  fileIds: number[]
  user:  {id: number, firstName: string, lastName: string}
  ticketId: number,
  workstepId: number
}

export interface EmailFileSet{
  ticketAttachment?: Attachment
  file?: File
  fileName?: string
}

@Injectable({
  providedIn: 'root'
})
export class EmailService {

  public mailboxes: Mailbox[] = []
  public templates: MailTemplate[] = []

  public selectedMailbox: Mailbox = null
  public selectedTemplate: MailTemplate = null

  public subject: string = ''

  public showCC: boolean = false
  public showBCC: boolean = false

  public emailTagsCC: EmailTags = new EmailTags(this.http)
  public emailTagsTo: EmailTags = new EmailTags(this.http)
  public emailTagsBCC: EmailTags = new EmailTags(this.http)

  public files: EmailFileSet[] = []

  public contentId: number = -1

  public templateChanged: BehaviorSubject<{html: string, override?: boolean}> = new BehaviorSubject<{html: string, override?: boolean}>(null)

  private get replaceTags(): {tag: string, replaceText: string}[]{
    return [
      {tag: "[TICKETID]", replaceText: this.ticketSrv.ticket.id?.toString()},
      {tag: "[Ticketnummer]", replaceText: this.ticketSrv.ticket.number?.toString()},
      {tag: "[TICKETBETREFF]", replaceText: this.ticketSrv.ticket.description},
      {tag: "[Arbeitsschritt_Nr]", replaceText: this.ticketSrv.activeWorkstep.number?.toString()},
      {tag: "[Arbeitsschritt_Bezeichnung]", replaceText: this.ticketSrv.activeWorkstep.description},
      {tag: "[Mitarbeiter_Name]", replaceText: this.authSrv.userNameFromToken},
      {tag: "[FORTSCHRITT]", replaceText: this.ticketSrv.process?.toString()},
      {tag: "[STATUS]", replaceText: this.ticketSrv.ticketStateDescription},
      {tag: "[TERMIN]", replaceText: this.ticketSrv.appointment ? `${dayjs(this.ticketSrv.appointment.time).format("DD.MM.yyyy HH:mm")} Uhr` : ''},
      {tag: "[AP_Anrede]", replaceText: this.ticketSrv.contactPerson?.salutation || ""},
      {tag: "[AP_Vorname]", replaceText: this.ticketSrv.contactPerson?.firstName || ""},
      {tag: "[AP_Name]", replaceText: this.ticketSrv.contactPerson?.lastName || ""},
      {tag: "[Mitarbeiter_Antwort]", replaceText: this.contentId == -1 ? "" : this.ticketSrv.workstepContent.find(c=>c.id == this.contentId)?.htmlText},
      {tag: "[AP_Abteilung]", replaceText: this.ticketSrv.contactPerson?.department || ""},
    ]
  } 

  constructor(
    private http: HttpService,
    private feedback: FeedbackService,
    private ticketSrv: TicketEmployee.TicketService,
    private authSrv: AuthService
  ) { 

  }

  /**
   * Setzt die Standardwerte für die E-Mail-Tags und zeigt die CC- und BCC-Felder nicht an.
   * Wenn eine Kontakt-E-Mail-Adresse vorhanden ist, wird diese als Empfänger festgelegt.
   * @returns {void}
   */
  public setDefaults(){
    let emailTag: EmailTag = null
    if(this.ticketSrv?.contactPerson?.email) emailTag = {firstName: this.ticketSrv.contactPerson?.firstName || "", lastName: this.ticketSrv.contactPerson?.lastName || "", email: this.ticketSrv.contactPerson.email}
    else if(this.ticketSrv.ticket?.address?.email) emailTag = {firstName: this.ticketSrv.ticket.address?.firstName || "", lastName: this.ticketSrv.ticket.address?.lastName || "", email: this.ticketSrv.ticket.address.email}
    this.emailTagsTo.tags = []
    this.emailTagsCC.tags = []
    this.emailTagsBCC.tags = []

    this.showCC = false
    this.showBCC = false

    if(!emailTag) return
    this.emailTagsTo.addTag(emailTag)
  }
  
  /**
   * Ruft die API-Daten über einen GET-Request ab.
   * @param path - Der Pfad zur API-Endpunkt.
   * @returns Ein Promise, das ein HttpFunctionResult-Objekt mit den abgerufenen Daten zurückgibt.
   * @template T - Der Typ der zurückgegebenen Daten.
   */
  protected async getAPIData<T>(path: string): Promise<HttpFunctionResult<T>>{
    return await this.http.GET<T>({path: path})
  }

  /**
   * Ruft die Liste der E-Mail-Postfächer ab.
   * @returns Ein Promise, das ein HttpFunctionResult-Objekt mit einem booleschen Wert zurückgibt, der angibt, ob die Anfrage erfolgreich war.
   */
  public async setTemplates(): Promise<HttpFunctionResult<any>>{
    const result: HttpFunctionResult<MailTemplate[]> = await this.getAPIData<MailTemplate[]>('ticket/email/templates')
    if(result.success) this.templates = result.data
    return result
  }

  /**
   * Ruft die Liste der E-Mail-Postfächer ab.
   * @returns Ein Promise, das ein HttpFunctionResult-Objekt mit einem booleschen Wert zurückgibt, der angibt, ob die Anfrage erfolgreich war.
   */
  public async setMailboxes(): Promise<HttpFunctionResult<any>>{
    const result: HttpFunctionResult<Mailbox[]> = await this.getAPIData<Mailbox[]>('ticket/email/mailboxes')
    if(result.success) this.mailboxes = result.data

    if(this.mailboxes.length > 0) {
      this.selectedMailbox = this.mailboxes[0]
      this.selectedTemplate = this.templates.find(t=>t.id === this.selectedMailbox.templateId) || this.templates[0]
      this.subject = this.replaceTextByTags(this.selectedTemplate.subject)
      await this.loadTemplateHtml(true)
    }

    return result
  }

  /**
   * Öffnet ein Popover, das eine Liste von Mailboxen anzeigt, aus denen der Benutzer auswählen kann.
   * Setzt die ausgewählte Mailbox als aktuelle Mailbox.
   * @param event Das Ereignis, das das Öffnen des Popovers ausgelöst hat.
   * @returns Eine Promise, die gelöst wird, wenn das Popover geschlossen wird.
   */
  public async selectMailbox(event: any){
    const list: PopoverListItem[] = this.mailboxes.map(m=>({id: m.id, description: m.description})) 
    await this.feedback.openPopover(PopoverPage, event, {list: list}, (result: HttpFunctionResult<PopoverListItem>)=>{
      if(!result.success) return
      this.selectedMailbox = this.mailboxes.find(m=>m.id === result.data.id)
    })
  }

  /**
   * Öffnet ein Popover mit einer Liste von Vorlagen und ermöglicht es dem Benutzer, eine Vorlage auszuwählen.
   * @param event Das Ereignis, das das Popover auslöst.
   * @returns Eine Promise, die das ausgewählte Template enthält.
   */
  public async selectTemplate(event: any){
    const list: PopoverListItem[] = this.templates.map(t=>({id: t.id, description: t.description})) 
    await this.feedback.openPopover(PopoverPage, event, {list: list}, (result: HttpFunctionResult<PopoverListItem>)=>{
      if(!result.success) return
      
      
      const newSelectedTemplate: MailTemplate = this.templates.find(t=>t.id === result.data.id)
      
      if(newSelectedTemplate.id === this.selectedTemplate?.id) return
      this.selectedTemplate = newSelectedTemplate
      this.subject = this.replaceTextByTags(this.selectedTemplate.subject)
      this.loadTemplateHtml(true)
    })
  }

  /**
   * Lädt die HTML-Vorlage für die ausgewählte Vorlage.
   * @param override Gibt an, ob die aktuelle Vorlage überschrieben werden soll.
   */
  private async loadTemplateHtml(override: boolean = false){
    const result: HttpFunctionResult<string> = await this.getAPIData<string>(`ticket/email/templates/${this.selectedTemplate.id}?workstepId=${this.ticketSrv.activeWorkstepId}`)    
    if(result.success) this.templateChanged.next({html: this.replaceEmployeeAnswer(result.data), override: override})
  }

  /**
   * Ersetzt den Platzhalter "[Mitarbeiter_Antwort]" im HTML-Text durch den entsprechenden Inhalt aus dem Ticket-Service.
   * @param html Der HTML-Text, in dem der Platzhalter ersetzt werden soll.
   * @returns Der HTML-Text mit dem ersetzen Platzhalter.
   */
  private replaceEmployeeAnswer(html: string): string{
    if(this.contentId < 0) return html
    else return html.replace("[Mitarbeiter_Antwort]", this.ticketSrv.workstepContent.find(c=>c.id == this.contentId)?.htmlText)
  }

  /**
   * Fügt die E-Mail-Adresse des aktuellen Benutzers als Empfänger hinzu.
   */
  private replaceTextByTags(text: string): string{
    this.replaceTags.forEach(tag=> text = text.replace(tag.tag, tag.replaceText))
    return text
  }

  /**
   * Fügt eine Liste von Dateien zum aktuellen Dateisatz hinzu.
   * @param dataSet - Das Datensatzobjekt, das die hinzuzufügenden Dateien enthält.
   */
  public async addFile(dataSet: any){
    for(const file of dataSet.files){
      const attachmentFiles: Attachment[] = this.ticketSrv.ticket.attachments.filter(a=>a.description === file.name)
      if(attachmentFiles.length > 0){
        const requestResult: boolean = await this.feedback.confirm(`Die Datei "${file.name}" wurde bereits dem Ticket beigefügt. Die Datei aus dem Ticket hinzufügen oder die Datei nochmal hochladen?`, "Aus Ticket hinzufügen.", "Erneut hochladen.")
        if(requestResult) this.files.push({fileName: file.name, ticketAttachment: attachmentFiles[0]})
        else{
          const [baseName, extension] = file.name.split('.');
          const suffixFileName =  extension ? `${baseName}_1.${extension}` : `${file.name}_1`;
          this.files.push({file: file, fileName: suffixFileName})
        } 
      }else
        if(file.size <= 20000000) this.files.push({file: file, fileName: file.name}) 
        else await this.feedback.showMsg(MsgType.Error, `Die Datei "${file.name}" ist zu groß. Die maximale Dateigröße beträgt 20 MB.`)
    }
  }

  /**
   * Sendet eine E-Mail mit den angegebenen Empfängern, Betreff, Inhalt und Anhängen.
   * @param editor - Der Editor, der den Inhalt der E-Mail enthält.
   * @returns Eine Promise, die bei erfolgreichem Versand der E-Mail aufgelöst wird.
   */
  public async sendMail(editor: any): Promise<HttpFunctionResult<WorkstepContent>>{

    //Abfragen ob Empfänger vorhanden
    if(this.emailTagsTo.tags.length == 0){
      await this.feedback.showError("Bitte geben Sie mindestens einen Empfänger an.")
      return {success: false} 
    }

    //Abfragen ob Inhalt vorhanden
    const plainText =editor.getText();
    if(plainText == "" || plainText == "\n"){
      await this.feedback.showError("Der Inhalt der E-Mail ist leer. Bitte geben Sie einen Inhalt an")
      return {success: false}
    }

    //Abfragen ob Betreff vorhanden
    if(!this.subject){
      const subjectConfirmResult: boolean = await this.feedback.confirm("Der Betreff ist leer. Möchten Sie die E-Mail trotzdem senden?", "Senden", "Abbrechen", "Betreff ist leer.")
      if(!subjectConfirmResult) return {success: false}
    }

    const attachmentResult: HttpFunctionResult<number[]> = await this.uploadAttachments()
    if(!attachmentResult.success) return {success: false, message: attachmentResult.message}

    await this.feedback.updateLoading("E-Mail wird gesendet...")

    const sendingWorkstepMail: SendingWorkstepMail = {
      mailboxId: this.selectedMailbox.id,
      to: this.emailTagsTo.tags.map(t=>t.email),
      cc: this.showCC ? this.emailTagsCC.tags.map(t=>t.email) : [],
      bcc: this.showBCC ? this.emailTagsBCC.tags.map(t=>t.email) : [],
      subject: this.subject,
      html: `<body>${editor.root.innerHTML}</body>`,
      plainText: editor.getText(),
      fileIds: this.files.filter(f=>f.ticketAttachment).map(f=>f.ticketAttachment.id),
      user: {id: +this.authSrv.getUser().id, firstName: this.authSrv.getUser()?.firstName, lastName: this.authSrv.getUser()?.firstName},
      ticketId: this.ticketSrv.ticket.id,
      workstepId: this.ticketSrv.activeWorkstepId,
    }

    const result: HttpFunctionResult<WorkstepContent> = await this.http.POST<SendingWorkstepMail, WorkstepContent>({
      path: 'ticket/email/send',
      body: sendingWorkstepMail
    })
    
    if(result.success) {
      this.ticketSrv.workstepContent.unshift(result.data)
      this.ticketSrv.workstepContentWithAttachment.unshift({type: "content", content: result.data})
      this.setDefaultMailSettings()
    }
    await this.feedback.hideLoading()
    return result
  }

  /**
   * Lädt die angehängten Dateien des Tickets hoch.
   * @returns Eine Promise, die ein HttpFunctionResult-Objekt mit einem Array von Datei-IDs zurückgibt, wenn der Upload erfolgreich ist.
   * Wenn keine Dateien vorhanden sind, wird ein leeres Array zurückgegeben.
   */
  private async uploadAttachments(): Promise<HttpFunctionResult<number[]>>{
    if(this.files.filter(f => f.file).length == 0) return {success: true, data: []}
    let fileIds: number[] = []
    await this.feedback.updateLoading("Anhänge hochgeladen...")
    return new Promise<HttpFunctionResult<number[]>>(async (resolve)=>{
      for(let file of this.files){
        await new Promise(resolveRequest=>{
          if(file.file){
            const formData: FormData = new FormData()
            formData.append('files', file.file, btoa(unescape(encodeURIComponent(file.fileName))))
            this.http.UPLOAD(`ticket/file?ticketid=${this.ticketSrv.ticket.id}&workstepid=${this.ticketSrv.activeWorkstepId}`, formData).subscribe((res: any)=>{
              if(res.type === HttpEventType.UploadProgress)  this.feedback.updateLoadingContent(`Anhänge hochladen: ${file.file.name} (${Math.round(res.loaded / res.total * 100)}%)`)
              else if(res.type === HttpEventType.Response){
                fileIds.push(res.body.data)
                const ticketAttachment: Attachment = {id: res.body.data, description: file.file.name, type: "Datei", date: new Date(), workstepId: this.ticketSrv.activeWorkstepId}
                this.ticketSrv.ticket.attachments.push(ticketAttachment)
                file.file = null
                file.ticketAttachment = ticketAttachment
                resolveRequest({success: true})
              }
            }, async (error: HttpErrorResponse)=>{
              await this.feedback.hideLoading()
              return resolve(await this.http.handleError(error))
            })
          } else resolveRequest({success: true})
        })
      }
      await this.feedback.hideLoading()
      resolve({success: true, data: fileIds})
    })

  }

  public setDefaultMailSettings(){
    this.emailTagsTo.tags = []
    this.emailTagsCC.tags = []
    this.emailTagsBCC.tags = []
    this.showCC = false
    this.showBCC = false
    this.subject = ''
    this.files = []
    this.contentId = -1
    this.templateChanged.next({html: "", override: true})
  }

}
